import Phaser from 'phaser';
import { AssetGenerator } from '../utils/asset-generator';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Show loading bar
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x222244, 0.8);
    progressBox.fillRect(width / 2 - 160, height / 2 - 15, 320, 30);

    const loadingText = this.add.text(width / 2, height / 2 - 40, 'Growing seeds...', {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#6aaa3a',
    });
    loadingText.setOrigin(0.5, 0.5);

    this.load.on('progress', (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(0x6aaa3a, 1);
      progressBar.fillRect(width / 2 - 155, height / 2 - 10, 310 * value, 20);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
    });
  }

  create(): void {
    // Generate all procedural pixel art assets
    AssetGenerator.generateAll(this);

    // Create animations
    this.createAnimations();

    // Transition to menu
    this.scene.start('MenuScene');
  }

  private createAnimations(): void {
    // Player walk animations (4 directions x 4 frames)
    const directions = ['down', 'left', 'right', 'up'];
    directions.forEach((dir, row) => {
      this.anims.create({
        key: `player-walk-${dir}`,
        frames: Array.from({ length: 4 }, (_, i) => ({
          key: 'player',
          frame: row * 4 + i,
        })),
        frameRate: 8,
        repeat: -1,
      });

      this.anims.create({
        key: `player-idle-${dir}`,
        frames: [{ key: 'player', frame: row * 4 }],
        frameRate: 1,
      });
    });

    // Plant growth animations (11 plant types: 6 base + 5 hybrid)
    const plantNames = [
      'sunflower', 'moonbloom', 'fire_pepper', 'shadow_vine', 'apple_tree', 'pear_tree',
      'peapple', 'twilight_blossom', 'infernal_creeper', 'solar_flare', 'void_orchid',
    ];
    plantNames.forEach((name, row) => {
      // Growth stages are individual frames, not animations
      for (let stage = 0; stage < 4; stage++) {
        this.anims.create({
          key: `plant-${name}-stage${stage}`,
          frames: [{ key: 'plants', frame: row * 4 + stage }],
          frameRate: 1,
        });
      }
    });

    // Water animation
    this.anims.create({
      key: 'water-flow',
      frames: Array.from({ length: 4 }, (_, i) => ({
        key: 'terrain',
        frame: 8 + i,
      })),
      frameRate: 4,
      repeat: -1,
    });

    // Monster walk animations (5 monster types x 4 frames)
    const monsterNames = ['piranha_plant', 'thorn_crawler', 'fungal_brute', 'wilt_wraith', 'root_titan'];
    monsterNames.forEach((name, row) => {
      this.anims.create({
        key: `monster-${name}-walk`,
        frames: Array.from({ length: 4 }, (_, i) => ({
          key: 'monsters',
          frame: row * 4 + i,
        })),
        frameRate: 6,
        repeat: -1,
      });
    });
  }
}
